import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocalStorage } from './useLocalStorage';

export interface ChatSoundSettings {
  muted: boolean;
  volume: number; // 0–100
  chatEnabled: boolean;
  diceEnabled: boolean;
}

const STORAGE_KEY = 'roll1-chat-sounds';

const DEFAULT_SETTINGS: ChatSoundSettings = {
  muted: false,
  volume: 80,
  chatEnabled: true,
  diceEnabled: true,
};

function getOrCreateAudioContext(ref: React.MutableRefObject<AudioContext | null>): AudioContext | null {
  if (ref.current && ref.current.state !== 'closed') return ref.current;
  try {
    ref.current = new AudioContext();
    return ref.current;
  } catch {
    return null;
  }
}

/** Soft two-tone ping for incoming chat messages */
// A5 (880 Hz) → C#6 (1100 Hz) gives a pleasant rising interval
const PING_FREQ_START_HZ = 880;
const PING_FREQ_END_HZ = 1100;
const PING_TRANSITION_SEC = 0.08;
const PING_DURATION_SEC = 0.35;

function playChatPing(ctx: AudioContext, volume: number): void {
  const now = ctx.currentTime;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(volume * 0.25, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + PING_DURATION_SEC);
  gain.connect(ctx.destination);

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(PING_FREQ_START_HZ, now);
  osc.frequency.setValueAtTime(PING_FREQ_END_HZ, now + PING_TRANSITION_SEC);
  osc.connect(gain);
  osc.start(now);
  osc.stop(now + PING_DURATION_SEC);
}

/** Dice-rattle percussive sound using noise burst + envelope */
// Tuning constants for the rattle timbre
const RATTLE_NOISE_DECAY_RATE = 8;    // higher = shorter decay on noise burst
const RATTLE_FREQ_HZ = 180;           // low periodic component for wood-on-wood feel
const RATTLE_FREQ_AMPLITUDE = 0.15;   // mix level of the periodic component
const RATTLE_FREQ_DECAY_RATE = 12;    // how fast the periodic component fades
const RATTLE_DURATION_SEC = 0.55;

function playDiceRattle(ctx: AudioContext, volume: number): void {
  const now = ctx.currentTime;
  const sampleRate = ctx.sampleRate;
  const bufferSize = Math.floor(sampleRate * RATTLE_DURATION_SEC);

  const buffer = ctx.createBuffer(1, bufferSize, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    const t = i / sampleRate;
    const decay = Math.exp(-t * RATTLE_NOISE_DECAY_RATE);
    const rattle = Math.sin(2 * Math.PI * RATTLE_FREQ_HZ * t) * RATTLE_FREQ_AMPLITUDE * Math.exp(-t * RATTLE_FREQ_DECAY_RATE);
    data[i] = (Math.random() * 2 - 1) * decay * 0.85 + rattle;
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 1200;
  filter.Q.value = 0.8;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(volume * 0.6, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + RATTLE_DURATION_SEC);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);

  source.start(now);
}

export interface UseChatSoundsReturn {
  settings: ChatSoundSettings;
  setSettings: (s: ChatSoundSettings) => void;
  playMessageSound: () => void;
  playDiceSound: () => void;
  warmUp: () => void;
}

export function useChatSounds(): UseChatSoundsReturn {
  const [settings, setSettings] = useLocalStorage<ChatSoundSettings>(STORAGE_KEY, DEFAULT_SETTINGS);
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Create & resume AudioContext on first user interaction (browser autoplay policy)
  const warmUp = useCallback(() => {
    const ctx = getOrCreateAudioContext(audioCtxRef);
    if (ctx?.state === 'suspended') ctx.resume().catch(() => undefined);
  }, []);

  useEffect(() => {
    const handler = () => {
      warmUp();
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('keydown', handler);
    };
    document.addEventListener('pointerdown', handler);
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('keydown', handler);
    };
  }, [warmUp]);

  const playMessageSound = useCallback(() => {
    if (settings.muted || !settings.chatEnabled) return;
    const ctx = getOrCreateAudioContext(audioCtxRef);
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);
      playChatPing(ctx, settings.volume / 100);
    } catch {
      // ignore audio errors — browser may block autoplay
    }
  }, [settings]);

  const playDiceSound = useCallback(() => {
    if (settings.muted || !settings.diceEnabled) return;
    const ctx = getOrCreateAudioContext(audioCtxRef);
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);
      playDiceRattle(ctx, settings.volume / 100);
    } catch {
      // ignore audio errors
    }
  }, [settings]);

  return useMemo(
    () => ({ settings, setSettings, playMessageSound, playDiceSound, warmUp }),
    [settings, setSettings, playMessageSound, playDiceSound, warmUp],
  );
}
